import type { AuditEntryPublic, SessionUser } from '@accounts/shared';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { STR } from '../../../i18n';
import { listAccessLevels, listAudit } from '../../../lib/api';
import { relativeTime } from '../../../lib/format';
import { queryKeys } from '../../../lib/queryKeys';
import { surfaceById } from '../../../lib/surfaces';
import { Callout, Card, CardHeader, EmptyRow, Skeleton } from '../../../ui/Primitives';
import { eyebrowStyle, monoStyle, quietButtonStyle, tileStyle } from '../../../ui/styles';

import { itemLabel } from './accessFormat';
import { useItEmails, useItStanding } from './itEmail/useItEmails';
import { WAITING_ON_IT_LANE, waitingOnIt } from './itEmail/waiting';
import { laneCounts, type OverviewTileKey, overviewTileKeys } from './overviewTiles';
import { isLimitedManager, useRoleQueue } from './queue/useRoleQueue';

/**
 * THE ACCESS OVERVIEW (FINAL demo R3-2): how much is waiting, by kind, and where. Every
 * total is a link into the Queue on its lane (`?type=`), so the Overview never offers an
 * action of its own — the work is done in one place. Its numbers come from the same
 * role-scoped read as the Queue's lanes, so a tile can never promise more than its lane holds.
 *
 * App deactivations are an administrator-only work lane. Waiting on IT (spec row 18) counts
 * the open items in sent IT emails, from the same read as the Queue's Waiting on IT lane; a
 * record in a sent email is counted there, not in its own lane, as the Queue draws it. So the
 * lane and Overdue totals wait for the read of which records are with IT: until it answers they
 * show a dash, never a count that holds the same record twice.
 */

const RECENT_LIMIT = 8;
const LONGEST_LIMIT = 7;

const tileLinkStyle: CSSProperties = {
  ...tileStyle,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  textDecoration: 'none',
  color: 'var(--text)',
};

const listRow: CSSProperties = {
  display: 'grid',
  gap: 10,
  alignItems: 'center',
  padding: '9px 18px',
  borderBottom: '1px solid var(--border)',
  fontSize: 12.5,
};

export function OverviewPanel({ user }: { user: SessionUser }) {
  const queue = useRoleQueue(user);
  const admin = !isLimitedManager(user);
  const queuePath = surfaceById('accessQueue').path;
  const tileKeys = overviewTileKeys(admin);
  const it = useItStanding();
  const itEmails = useItEmails();

  const levels = useQuery({
    queryKey: queryKeys.accessLevels,
    queryFn: () => listAccessLevels(),
    enabled: admin,
  });

  /*
   * The audit log is admin-gated, so HR is never sent to read it. Two groups, two reads,
   * merged newest first: the server resolves a group, never a client-side prefix match.
   */
  const recent = useQueries({
    queries: (['access', 'requests'] as const).map((group) => ({
      queryKey: queryKeys.audit({ group, q: '', from: '', to: '', page: 0, limit: RECENT_LIMIT }),
      queryFn: () => listAudit({ group, limit: RECENT_LIMIT, offset: 0 }),
      enabled: admin,
    })),
  });
  const recentLoading = recent.some((result) => result.isLoading);
  const recentEntries: AuditEntryPublic[] = recent
    .flatMap((result) => result.data?.data ?? [])
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))
    .slice(0, RECENT_LIMIT);

  if (queue.isLoading) {
    return (
      <div style={{ padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Skeleton style={{ height: 18, width: 220, borderRadius: 6 }} />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${String(tileKeys.length)},minmax(0,1fr))`,
            gap: 12,
          }}
        >
          {tileKeys.map((key) => (
            <Skeleton key={key} style={{ height: 86, borderRadius: 12 }} />
          ))}
        </div>
        <Skeleton style={{ height: 220, borderRadius: 12 }} />
      </div>
    );
  }

  if (queue.isError) {
    return (
      <div style={{ padding: '28px 32px' }}>
        <Callout
          tone="danger"
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            onClick={queue.refetch}
          >
            {STR.common.tryAgain}
          </button>
        </Callout>
      </div>
    );
  }

  const items = queue.items;
  const deactivations = queue.deactivations;
  // Until its read answers, a total shows a dash, never a 0 or a record counted twice.
  const lanes = it.ready ? laneCounts(items, it.standing) : null;
  const waiting = itEmails.data === undefined ? null : waitingOnIt(itEmails.data.data);
  // As the Queue's `?type=overdue` view: records in a sent IT email are not in a lane.
  const totalOverdue =
    lanes === null
      ? null
      : Object.values(lanes).reduce((sum, lane) => sum + lane.overdue, 0) +
        queue.deactivationCounts.overdue;
  const standingFailed = !it.ready && it.isError;
  const emailsFailed = itEmails.data === undefined && itEmails.isError;
  const oldestAccessAt = items[0]?.since;
  const oldestDeactivationAt = deactivations[0]?.createdAt;
  const oldestAt =
    oldestAccessAt === undefined
      ? oldestDeactivationAt
      : oldestDeactivationAt === undefined || oldestAccessAt <= oldestDeactivationAt
        ? oldestAccessAt
        : oldestDeactivationAt;

  // "Where the work is": setup and removal items by their row's top-level node.
  const topLevel = levels.data?.data.find((level) => level.depth === 1) ?? null;
  const byTop = new Map<string, { name: string; n: number }>();
  for (const item of items) {
    if (item.type !== 'needs_access' && item.type !== 'remove_access') continue;
    const top = item.path[0];
    if (top === undefined) continue;
    const entry = byTop.get(top.id) ?? { name: top.name, n: 0 };
    entry.n += 1;
    byTop.set(top.id, entry);
  }
  const work = [...byTop.values()].sort((a, b) => b.n - a.n);
  const most = work[0]?.n ?? 0;

  const tile = (type: OverviewTileKey, label: string, n: number | null, sub: string) => (
    <Link
      key={type}
      to={`${queuePath}?type=${type}`}
      className="hov"
      aria-label={STR.access.overview.tileOpen(label, n ?? STR.common.dash)}
      style={{
        ...tileLinkStyle,
        ...(type === 'overdue' && (n ?? 0) > 0 ? { borderColor: 'var(--bg-danger)' } : {}),
      }}
    >
      <span className="tx-subtle" style={eyebrowStyle}>
        {label}
      </span>
      <span
        className={type === 'overdue' && (n ?? 0) > 0 ? 'tx-danger-strong' : undefined}
        style={{ fontSize: 24, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}
      >
        {n ?? STR.common.dash}
      </span>
      <span className="tx-subtle" style={{ fontSize: 11.5 }}>
        {sub}
      </span>
    </Link>
  );

  function tileFor(key: OverviewTileKey): ReactNode {
    switch (key) {
      case 'deactivations':
        return tile(
          key,
          STR.access.overview.tile.deactivations,
          queue.deactivationCounts.open,
          STR.access.overview.tileOverdue(queue.deactivationCounts.overdue),
        );
      case WAITING_ON_IT_LANE:
        return tile(
          key,
          STR.access.overview.tile.it,
          waiting?.open ?? null,
          waiting === null ? STR.common.dash : STR.access.overview.tileOverdue(waiting.overdue),
        );
      case 'overdue':
        return tile(
          key,
          STR.access.overview.tile.overdue,
          totalOverdue,
          STR.access.overview.tileOldest(
            oldestAt === undefined ? STR.common.dash : relativeTime(oldestAt),
          ),
        );
      default:
        return tile(
          key,
          STR.access.overview.tile[key],
          lanes === null ? null : lanes[key].n,
          lanes === null ? STR.common.dash : STR.access.overview.tileOverdue(lanes[key].overdue),
        );
    }
  }

  return (
    <div
      style={{
        padding: '28px 32px 110px',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
        maxWidth: 1080,
      }}
    >
      <div>
        <div style={{ fontSize: 19, fontWeight: 650, letterSpacing: '-.015em' }}>
          {STR.access.overview.heading}
        </div>
        {totalOverdue === null ? (
          <Skeleton style={{ height: 14, width: 320, borderRadius: 5, marginTop: 4 }} />
        ) : (
          <div className="tx-subtle" style={{ fontSize: 13, marginTop: 2 }}>
            {STR.access.overview.summary(items.length + deactivations.length, totalOverdue)}
          </div>
        )}
      </div>

      {admin ? null : <Callout tone="neutral">{STR.access.overview.hrNote}</Callout>}

      {standingFailed || emailsFailed ? (
        <Callout
          tone="danger"
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <span style={{ flex: 1, minWidth: 180 }}>{STR.access.overview.itCountsFailed}</span>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            onClick={() => {
              if (standingFailed) it.refetch();
              if (emailsFailed) void itEmails.refetch();
            }}
          >
            {STR.common.tryAgain}
          </button>
        </Callout>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${String(tileKeys.length)},minmax(0,1fr))`,
          gap: 12,
        }}
      >
        {tileKeys.map(tileFor)}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: admin ? 'minmax(0,1.2fr) minmax(0,1fr)' : 'minmax(0,1fr)',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card>
          <CardHeader
            title={STR.access.overview.waitingLongest}
            action={
              <Link to={queuePath} className="tbtn" style={{ ...quietButtonStyle, textDecoration: 'none' }}>
                {STR.access.overview.openQueue}
              </Link>
            }
          />
          {items.length === 0 ? (
            <EmptyRow>{STR.access.overview.nothingWaiting}</EmptyRow>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {items.slice(0, LONGEST_LIMIT).map((item) => (
                <li
                  key={`${item.type}:${item.id}`}
                  style={{
                    ...listRow,
                    gridTemplateColumns: '92px minmax(0,1fr)',
                    ...(item.overdue ? { boxShadow: 'inset 3px 0 0 var(--bg-danger)' } : {}),
                  }}
                >
                  <span
                    className={item.overdue ? 'tx-danger-strong' : 'tx-subtle'}
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: item.overdue ? 700 : 400,
                      fontSize: 11.5,
                    }}
                  >
                    {item.overdue
                      ? STR.access.queue.overdueAge(relativeTime(item.since))
                      : relativeTime(item.since)}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 650 }}>{item.user.name}</span>
                    <span
                      className="tx-subtle"
                      style={{
                        display: 'block',
                        fontSize: 11.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {STR.access.overview.itemLine(
                        STR.access.queue.type[item.type],
                        itemLabel(item.path, item.catalogItem.name),
                      )}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {admin ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card>
              <CardHeader
                title={
                  topLevel === null
                    ? STR.access.overview.openWorkByFallback
                    : STR.access.overview.openWorkBy(topLevel.name)
                }
                action={
                  <Link
                    to={surfaceById('accessMatrix').path}
                    className="tbtn"
                    style={{ ...quietButtonStyle, textDecoration: 'none' }}
                  >
                    {STR.access.overview.openMatrix}
                  </Link>
                }
              />
              {work.length === 0 ? (
                <EmptyRow>{STR.access.overview.noOpenWork}</EmptyRow>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {work.map((entry) => (
                    <li
                      key={entry.name}
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'center',
                        padding: '8px 18px',
                        borderBottom: '1px solid var(--border)',
                        fontSize: 12.5,
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{entry.name}</span>
                      <span
                        aria-hidden="true"
                        style={{
                          width: 120,
                          height: 6,
                          borderRadius: 999,
                          background: 'var(--track)',
                          overflow: 'hidden',
                        }}
                      >
                        <span
                          style={{
                            display: 'block',
                            height: 6,
                            width: `${String(Math.round((entry.n / Math.max(most, 1)) * 100))}%`,
                            background: 'var(--bg-warn)',
                          }}
                        />
                      </span>
                      <span
                        className="tx-warn-strong"
                        style={{
                          fontWeight: 650,
                          width: 24,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {entry.n}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card>
              <CardHeader title={STR.access.overview.recentChanges} />
              {recentLoading ? (
                <div style={{ padding: 16 }}>
                  <Skeleton style={{ height: 12, width: '70%', borderRadius: 5 }} />
                </div>
              ) : recentEntries.length === 0 ? (
                <EmptyRow>{STR.access.overview.noRecentChanges}</EmptyRow>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {recentEntries.map((entry) => (
                    <li
                      key={entry.id}
                      style={{
                        ...listRow,
                        gridTemplateColumns: 'minmax(0,1fr) auto',
                      }}
                    >
                      <span style={{ minWidth: 0 }}>
                        <span style={{ ...monoStyle, fontSize: 11.5 }}>{entry.action}</span>
                        <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5 }}>
                          {entry.actor?.name ?? STR.common.dash}
                        </span>
                      </span>
                      <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                        {relativeTime(entry.createdAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        ) : null}
      </div>
    </div>
  );
}
